// Libs
import { useSelector } from 'react-redux';
// Services
import ApiServiceV2 from 'services/apiService/apiServiceV2';
// Selectors
import {
	selectSelectedDataFilterId,
	selectSelectedOrderTargetFilterId,
} from 'core/selectors';
import {
	normalizeDatasourceAccessId,
	normalizePositiveIntegerId,
	ORDER_OVERVIEW_MODES,
} from 'core/datasourceAccess';
import { useEffect, useRef } from 'react';
import { requestTransactionCapabilities } from './transactionCapabilitiesApi';

const modulesEndpoint = '/modules';
const paymentsEndpoint = '/payments';
const paymentOrdersEndpoint = '/payments_orders';
// Inventory endpoints
const endpoint_im = '/inventoryManager';
const endpoint_i = '/items';
const endpoint_w = '/warehouses';
const endpoint_wis = '/warehouses_items_states';
const endpoint_wtd = '/warehouses_transactions_docs';
const endpoint_wtdi = '/warehouses_transactions_docs_items';
const endpoint_wp = '/warehouses_packages';
const useOrderApi = () => {
	const selectedDataFilterId = useSelector(selectSelectedDataFilterId);
	const selectedOrderTargetFilterId = useSelector(
		selectSelectedOrderTargetFilterId,
	);

	const fechControllerRef = useRef(null);

	const abortLegazyFechState = () => {
		if (fechControllerRef.current) fechControllerRef.current.abort();
		const controller = new AbortController();
		fechControllerRef.current = controller;
		return controller.signal;
	};
	const getControlSignal = controlSignal =>
		controlSignal || abortLegazyFechState();

	useEffect(() => () => fechControllerRef.current?.abort(), []);

	return {
		// Billing
		getOrderDetails: ({ orderId, datasourceAccessId, orderOverviewMode }) => {
			const normalizedDatasourceAccessId =
				normalizeDatasourceAccessId(datasourceAccessId);
			const normalizedOrderId = normalizePositiveIntegerId(orderId);

			if (!normalizedOrderId)
				return Promise.reject({
					showError: true,
					message: 'No se pudo abrir la orden: identificadores inválidos',
					type: 'warn',
				});

			if (orderOverviewMode === ORDER_OVERVIEW_MODES.RESOURCE)
				return ApiServiceV2.post({
					url: `${modulesEndpoint}/v1/orders/${normalizedOrderId}/overview`,
					data: {},
				});

			if (normalizedDatasourceAccessId)
				return ApiServiceV2.post({
					url: `${modulesEndpoint}/v1/overview/${normalizedDatasourceAccessId}/${normalizedOrderId}`,
				});

			return Promise.reject({
				showError: true,
				message: 'No se pudo determinar el acceso a la orden',
				type: 'warn',
			});
		},
		getPaymentsDetails: ({ paycutId, searchValue, lastRowId, limit }) =>
			ApiServiceV2.post({
				url: `${paymentsEndpoint}/v1/getPayments`,
				data: { paycutId, searchValue, lastRowId, limit },
			}),
		getPaycutOverview: ({ paycutId, searchValue, lastRowId, limit }) =>
			ApiServiceV2.post({
				url: `${modulesEndpoint}/v1/overview/${selectedDataFilterId}/${paycutId}`,
				data: { searchValue, lastRowId, limit },
			}),
		getPaymentOrdersDetails: ({
			paymentId,
			orderId,
			orderFlowType,
			searchValue,
			lastRowId,
			limit,
		}) =>
			ApiServiceV2.post({
				url: `${paymentOrdersEndpoint}/v1/getOrders`,
				data: {
					paymentId,
					orderId,
					orderFlowType,
					searchValue,
					lastRowId,
					limit,
				},
			}),
		getPaymentOrdersList: ({ paymentId, orderFlowType }) =>
			ApiServiceV2.post({
				url: `${paymentOrdersEndpoint}/getOrders`,
				data: {
					payIds: [paymentId],
					getOrders: orderFlowType,
				},
			}),
		importGponInstallationPricingSignals: formData =>
			ApiServiceV2.post({
				url: `${paymentOrdersEndpoint}/importGponInstallationPricingSignals`,
				data: formData,
			}),
		getCoinsData: () =>
			ApiServiceV2.get({
				url: `/coins`,
			}),

		// Inventory - Transactions
		getTrasactionDetails: (wtdId, controlSignal) => {
			return ApiServiceV2.post({
				url: `${modulesEndpoint}/v1/overview/${selectedDataFilterId}/${wtdId}`,
				controlSignal: getControlSignal(controlSignal),
			});
		},
		getWarehouseStock: (
			{
				warehouseId,
				datasourceAccessId,
				orderTargetId = selectedOrderTargetFilterId,
			},
			controlSignal,
		) =>
			ApiServiceV2.post({
				url: `${modulesEndpoint}/v1/overview/${
					datasourceAccessId || selectedDataFilterId
				}/${warehouseId}/${orderTargetId}`,
				controlSignal: getControlSignal(controlSignal),
			}),
		getWarehouseStockSeries: (
			{
				warehouseId,
				datasourceAccessId,
				orderTargetId = selectedOrderTargetFilterId,
				itemId,
				stateId,
			},
			controlSignal,
		) =>
			ApiServiceV2.post({
				url: `${modulesEndpoint}/v1/overview/${
					datasourceAccessId || selectedDataFilterId
				}/${warehouseId}/${orderTargetId}/series/${itemId}/${stateId}`,
				controlSignal: getControlSignal(controlSignal),
			}),
		getWarehouseStockSeriesHistory: (
			{
				warehouseId,
				datasourceAccessId,
				orderTargetId = selectedOrderTargetFilterId,
				itemId,
				stateId,
				warehouseItemId,
			},
			controlSignal,
		) =>
			ApiServiceV2.post({
				url: `${modulesEndpoint}/v1/overview/${
					datasourceAccessId || selectedDataFilterId
				}/${warehouseId}/${orderTargetId}/series/${itemId}/${stateId}/${warehouseItemId}/history`,
				controlSignal: getControlSignal(controlSignal),
			}),
		changeWarehouseSeriesState: (
			{
				warehouseId,
				datasourceAccessId,
				orderTargetId = selectedOrderTargetFilterId,
				warehouseItemId,
				targetStateId,
			},
			controlSignal,
		) =>
			ApiServiceV2.post({
				url: `${modulesEndpoint}/v1/overview/${
					datasourceAccessId || selectedDataFilterId
				}/${warehouseId}/${orderTargetId}/series/${warehouseItemId}/state-changes`,
				data: { targetStateId },
				...(controlSignal ? { controlSignal } : {}),
			}),
		adjustWarehouseStock: (
			{
				warehouseId,
				datasourceAccessId,
				orderTargetId = selectedOrderTargetFilterId,
				itemId,
				targetAmount,
			},
			controlSignal,
		) =>
			ApiServiceV2.post({
				url: `${modulesEndpoint}/v1/overview/${
					datasourceAccessId || selectedDataFilterId
				}/${warehouseId}/${orderTargetId}/adjustments`,
				data: { itemId, targetAmount },
				controlSignal: getControlSignal(controlSignal),
			}),
		getArticles: controlSignal => {
			return ApiServiceV2.get({
				url: `${endpoint_i}`,
				controlSignal: getControlSignal(controlSignal),
			});
		},
		addNewArticleToWTD: (data, controlSignal) => {
			return ApiServiceV2.post({
				url: `${endpoint_wtdi}/v1/addNewItemsWTD`,
				data,
				...(controlSignal ? { controlSignal } : {}),
			});
		},
		deleteArticlesInTransit: (ids, controlSignal) => {
			const idsBuffer = Buffer.from(JSON.stringify(ids)).toString('base64');

			return ApiServiceV2.delete({
				url: `${endpoint_wtdi}/v1/deleteArticlesInTransit/${idsBuffer}`,
				...(controlSignal ? { controlSignal } : {}),
			});
		},
		deleteWtd: (wtdId, controlSignal) => {
			return ApiServiceV2.delete({
				url: `${endpoint_wtd}/${encodeURIComponent(wtdId)}`,
				controlSignal: getControlSignal(controlSignal),
			});
		},
		getItemStates: controlSignal => {
			return ApiServiceV2.get({
				url: `${endpoint_wis}`,
				controlSignal: getControlSignal(controlSignal),
			});
		},
		getZonesAreas: controlSignal => {
			return ApiServiceV2.get({
				url: `${endpoint_w}/zones-areas`,
				controlSignal: getControlSignal(controlSignal),
			});
		},
		getItemsInTransit: (wtdId, controlSignal) => {
			return ApiServiceV2.get({
				url: `${endpoint_wtdi}/v1/getItemsInTransit/${wtdId}`,
				controlSignal: getControlSignal(controlSignal),
			});
		},
		upsertArticle: ({ ids, fields }, controlSignal) => {
			return ApiServiceV2.post({
				url: `${endpoint_wtdi}/v1/upsertArticle`,
				data: { ids, fields },
				...(controlSignal ? { controlSignal } : {}),
			});
		},
		bulkImportTransactionItems: data =>
			ApiServiceV2.post({
				url: `${endpoint_wtdi}/v1/bulk-import`,
				data,
			}),
		createPakage: data => {
			return ApiServiceV2.post({
				url: `${endpoint_wp}`,
				data,
				controlSignal: getControlSignal(),
			});
		},
		closeWtd: wtdId => {
			return ApiServiceV2.post({
				url: `${endpoint_wtd}/closeWtd/${wtdId}`,
				controlSignal: getControlSignal(),
			});
		},
		finalizeWtd: (wtdId, controlSignal) => {
			return ApiServiceV2.post({
				url: `${endpoint_wtd}/${wtdId}/finalize`,
				controlSignal: getControlSignal(controlSignal),
			});
		},
		getTransactionCapabilities: (wtdId, controlSignal) =>
			requestTransactionCapabilities(wtdId, controlSignal),
		signWtd: (wtdId, action, controlSignal) => {
			return ApiServiceV2.put({
				url: `${endpoint_wtd}/sign`,
				data: {
					document_id: wtdId,
					...(action ? { action } : {}),
				},
				controlSignal: getControlSignal(controlSignal),
			});
		},
		getInventoryToTransfer: ({
			oWarehouseId,
			wtdId,
			wtdiId,
			orderTargetId = selectedOrderTargetFilterId,
			controlSignal,
		}) => {
			const ids = Buffer.from(
				JSON.stringify({
					level: 'transferable',
					warehouseId: oWarehouseId,
					wtdId,
					wtdiId,
					orderTargetId,
				}),
			).toString('base64');

			return ApiServiceV2.get({
				url: `${endpoint_im}/v2/stock/${ids}`,
				controlSignal: getControlSignal(controlSignal),
			});
		},
	};
};

export default useOrderApi;
